import React from 'react';
import PropTypes from 'prop-types';
import { FastField as FormikFieldComponent } from 'formik';
import { cloneObject } from '../../../common/utils';
import {
  allowedInputTypes,
  allowedDescriptionType,
  CustomInput
} from '../CustomInput';

const FormikInput = props => {
  const {
    name,
    label,
    type = 'text',
    description,
    wrapperClasses,
    isSwappedAlign,
    dateDefault,
    dateMin,
    dateMax,
    inputRef,
    checkPasswordStrength,
    options,
    noOptionsMessage,
    onInputChange,
    createOptionMessage,
    isMulti,
    isCreatable,
    workaroundSelectInitialValueCheck,
    placeSuggestionMeterRadius,
    placeSuggestionTypes,
    placeSearchLocation,
    onAddressChange,
    onAddressSelect,
    onGeocodeProcess,
    suggestionListTitle,
    onDayClick,
    onChange: customChangeHandler
  } = props;

  const inputAttributes = cloneObject(props, {}, [
    'label',
    'description',
    'isSwappedAlign',
    'dateDefault',
    'dateMin',
    'dateMax',
    'checkPasswordStrength',
    'options',
    'noOptionsMessage',
    'onInputChange',
    'createOptionMessage',
    'isMulti',
    'isCreatable',
    'placeSuggestionMeterRadius',
    'placeSuggestionTypes',
    'placeSearchLocation',
    'onAddressChange',
    'onAddressSelect',
    'onGeocodeProcess',
    'suggestionListTitle',
    'onDayClick',
    'onChange' // customChangeHandler
  ]);

  return (
    <FormikFieldComponent name={name}>
      {({
        field: receivedFieldProps,
        form: {
          setFieldValue,
          touched,
          isSubmitting,
          // values,
          initialValues,
          errors
        }
      }) => {
        const field = cloneObject(receivedFieldProps);

        if (field.value === undefined) {
          throw new Error(
            `Input value for "${inputAttributes.name}" could not be found. `
            + 'Did you format the initial form values correctly?'
          );
        }

        if (type === 'select') {
          // Replace the default Formik change event handler for select fields
          // TODO: is it worth it to investigate why it does not work with the default handler?
          field.onChange = function (passedNewValue) {
            let newValue = passedNewValue;

            if (!passedNewValue) {
              newValue = isMulti ? [] : { value: '' };
            }

            const addedNewOptionIndicator = '__isNew__';
            const foundNewOptionInArray = (newValue && isMulti) && [...newValue]
              .reverse()
              .find(({ [addedNewOptionIndicator]: isNew }) => isNew);
            const addedSingleNewOption = (newValue && newValue[addedNewOptionIndicator]) && newValue;
            const addedOption = foundNewOptionInArray || addedSingleNewOption;

            setFieldValue(inputAttributes.name, isMulti ? newValue.map(({ value }) => value) : newValue.value);

            if (addedOption) {
              options.push(addedOption);
            }
          };

          const { [name]: initialSelectValue } = initialValues;
          const isSelectValueArray = Array.isArray(initialSelectValue);

          const isInitialValueTypeValid = !isSelectValueArray
            ? typeof (initialSelectValue) === 'string'
            : !initialSelectValue.find(initialArrayMemberValue => typeof (initialArrayMemberValue) !== 'string');

          if (!isInitialValueTypeValid) {
            const invalidInitialValue = !isSelectValueArray ? initialSelectValue : initialSelectValue
              .find(initialArrayMemberValue => typeof (initialArrayMemberValue) !== 'string');

            const invalidValueType = typeof (invalidInitialValue);

            throw new Error(
              'ERROR: the initial select input type needs to be string (or array of string members in case of '
              + 'multi value.'
              + " The string value should match one of the available option objects' value property."
              + `

              Found invalid initial value type: ${
                isSelectValueArray ? `array of ${invalidValueType}s` : invalidValueType
                }
              Valid option: ${isSelectValueArray ? 'array of strings' : 'string'}`
            );
          }

          const ensureOptionForInitialValue = function (initialValue) {
            if (!options.find(({ value }) => initialValue === value)) {
              throw new Error(
                `ERROR: select option was not found for the provided initial value "${initialValue}"`
              );
            }
          };

          if (!workaroundSelectInitialValueCheck) {
            if (isSelectValueArray && initialSelectValue.length > 0) {
              initialSelectValue.forEach(multiValueMember => {
                ensureOptionForInitialValue(multiValueMember);
              });
            }

            else if (!isSelectValueArray && initialSelectValue !== '') {
              ensureOptionForInitialValue(initialSelectValue);
            }
          }
        }

        if (type === 'select-async') {
          field.onChange = function (selected) {
            setFieldValue(inputAttributes.name, Array.isArray(selected)
              ? selected.map(({ value: selectedValue, label: selectedLabel }) =>
                ({ value: selectedValue, label: selectedLabel }))
              : ({ value: selected.value, label: selected.label })
            );
          };
        }

        if (type === 'radio') {
          inputAttributes.checked = field.value === inputAttributes.value;
          delete field.value;
        }

        if (type === 'address') {
          const {
            value: {
              address,
              location: {
                latitude,
                longitude
              } = {}
            }
          } = field;

          if (address === undefined || latitude === undefined || longitude === undefined) {
            throw new Error(
              'Invalid value object passed to the address field - expects the following shape with defined props:'
              + ' {address: "", location: {latitude: 0, longitude: 0}}.'
              + ` Got: ${JSON.stringify(field.value)}`
              + ' Did you format the initial form values correctly?');
          }
        }

        const inputControllerProps = {
          label,
          description,
          errorMessage: touched[name] || isSubmitting ? errors[name] : null,
          isSwappedAlign,
          setFieldValue,
          dateDefault,
          dateMin,
          dateMax,
          inputRef,
          checkPasswordStrength,
          options,
          noOptionsMessage,
          onInputChange,
          createOptionMessage,
          isMulti,
          isCreatable,
          customChangeHandler,
          wrapperClasses,
          placeSuggestionMeterRadius,
          placeSuggestionTypes,
          placeSearchLocation,
          onAddressChange,
          onAddressSelect,
          onGeocodeProcess,
          suggestionListTitle,
          onDayClick,
          inputAttributes: {
            ...field,
            ...inputAttributes
          }
        };

        return <CustomInput {...inputControllerProps} />;
      }}
    </FormikFieldComponent>
  );
};

FormikInput.propTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(allowedInputTypes),
  label: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string,
    PropTypes.bool
  ]),
  placeholder: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool
  ]),
  description: allowedDescriptionType,
  wrapperClasses: PropTypes.arrayOf(PropTypes.string),
  isSwappedAlign: PropTypes.bool,
  dateDefault: PropTypes.object,
  dateMin: PropTypes.object,
  dateMax: PropTypes.object,
  isDatePickerOpen: PropTypes.bool,
  inputRef: PropTypes.object,
  workaroundSelectInitialValueCheck: PropTypes.bool, // for select-type TODO: get rid of this
  checkPasswordStrength: PropTypes.bool,
  isMulti: PropTypes.bool, // for select-type
  isCreatable: PropTypes.bool, // for select-type
  options: PropTypes.array, // for select-type
  loadOptions: PropTypes.func, // for select-type-asyncs
  noOptionsMessage: PropTypes.func, // for select-type
  onInputChange: PropTypes.func, // for select-type
  createOptionMessage: PropTypes.func, // for select-type when isCreatable is set true
  placeSuggestionMeterRadius: PropTypes.number, // for address-type
  placeSuggestionTypes: PropTypes.array, // for address-type
  placeSearchLocation: PropTypes.object, // for address-type
  onAddressChange: PropTypes.func, // for address-type
  onAddressSelect: PropTypes.func, // for address-type
  onGeocodeProcess: PropTypes.func, // for address-type
  suggestionListTitle: PropTypes.string, // for address-type
  onDayClick: PropTypes.func, // for date-type
  onChange: PropTypes.func // overwrites Formik's default onChange --> will be passed down as customChangeHandler
};

export default FormikInput;